import { isNil, pick } from 'lodash';
import type { MlsSearchResponse, MlsSearchResult } from './types';

type ResponseAgent = NonNullable<MlsSearchResponse['listingAgent']>;

const toAgent = (agent?: ResponseAgent): MlsSearchResult['listingAgent'] =>
  agent
    ? {
        firstName: agent.firstname,
        lastName: agent.lastname,
        cellPhone: agent.cell_phone,
        ...pick(agent, ['email', 'licenseNumber', 'brokerage']),
      }
    : null;

const toPhotos = (photos?: MlsSearchResponse['photos']): string[] =>
  Object.values(photos ?? {}).reduce<string[]>((agg, photo) => {
    if (!isNil(photo)) agg.push(`${photo}`);
    return agg;
  }, []);

/**
 * Reshapes the Home Junction payload core-service returns into the flat,
 * camelCase shape the cover-sheet autofill configurations address by path.
 * `property` and `photos` are never null: `action.props[].from` paths resolve
 * through them, and an empty `photos` has always reached the form as `[]`.
 */
export const toMlsSearchResult = (
  response: MlsSearchResponse,
): MlsSearchResult => ({
  property: {
    ...response.property?.location,
    assessorParcelNumber: response.property?.assessorParcelNumber,
    legalDescription: response.property?.legalDescription,
    type: response.property?.propertyType,
    transactionType: response.property?.transactionType,
    bedrooms: response.property?.beds,
    bathrooms: response.property?.baths?.total,
    buildingArea: response.property?.squareFeet,
    lotSize: response.property?.lotSquareFeet?.sqft,
    latitude: response.property?.coordinates?.latitude,
    longitude: response.property?.coordinates?.longitude,
  },
  listingAgent: toAgent(response.listingAgent),
  coListingAgent: toAgent(response.coListingAgent),
  photos: toPhotos(response.photos),
  description: response.description,
  tourUrl: response.tourUrl,
});
